import { useInstance } from '@meraki-internal/react-dependency-injection';
import { BufferedEventBus } from '../utils/BufferedEventBus';
import { APP_FOREGROUNDED } from '../AppContainer';
import { SocketState } from './SocketState';
import { isAutomatedTests } from '../utils/isAutomatedTests';

/**
 * E2E-only affordances for the live-note-update specs (IH-6930). Mounted globally in InnerhiveApp;
 * renders nothing for real users and a couple of off-screen buttons for automated tests, so the
 * test can drive them via the AppDriver instead of reaching into window.IOC (banned — unreliable
 * across pages). Not shipped to real users: isAutomatedTests() is only true when the E2E harness
 * sets the IS_AUTOMATED_TESTS localStorage flag (see wdio.common.conf.ts).
 *
 * - test-emit-app-foregrounded: emits APP_FOREGROUNDED on the BufferedEventBus, the exact signal a
 *   real native foreground produces. Exercises both subscribers — SocketState rebuilds the socket
 *   and ForegroundReconciler runs the CareMapState.reload + NotesModel.reconcile catch-up.
 * - test-close-socket: closes the live websocket (preventing retry) so a co-owner's change is NOT
 *   pushed live, isolating the foreground catch-up path under test.
 */
export const TestForegroundHook: React.FC = () => {
    const events = useInstance(BufferedEventBus);
    const socketState = useInstance(SocketState);

    if (!isAutomatedTests()) {
        return null;
    }

    // off-screen but interactable (display:none is not clickable in WebdriverIO); only ever rendered
    // under automated tests anyway
    const hiddenStyle: React.CSSProperties = { position: 'fixed', bottom: 0, left: 0, width: 1, height: 1, opacity: 0, zIndex: -1 };

    return (
        <>
            <button
                data-id="test-emit-app-foregrounded"
                onClick={() => events.emit(APP_FOREGROUNDED)}
                style={hiddenStyle}
            >
                emit foregrounded
            </button>
            <button
                data-id="test-close-socket"
                onClick={() => socketState.close()}
                style={hiddenStyle}
            >
                close socket
            </button>
        </>
    );
};
